import { useCallback, useEffect, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { crossedThresholds, viewClock, type ClockKey, type Round, type TimerCommand, type TimerRecord } from '@flow/core';
import { run, useDeps, useUseCases } from '../../app/services';
import { setWakeLock, timerBeep, unlockAudio } from '../../infra/browser';

/** The strip always flashes at these; sounds follow the judge's own choice. */
const FLASH_MS = [30_000, 10_000, 0];

/**
 * Live timer for a round. State lives in IndexedDB as timestamps; this hook just re-renders a few times
 * per second while a clock runs and fires alerts when thresholds are crossed.
 */
export const useTimer = (round: Round) => {
  const deps = useDeps();
  const uc = useUseCases();
  const rec = useLiveQuery(() => deps.timers.get(round.id), [round.id]);
  const settings = useLiveQuery(() => deps.settings.get(), []);
  const [now, setNow] = useState(() => Date.now());
  const [flash, setFlash] = useState<number | null>(null);
  const last = useRef(Date.now());

  useEffect(() => {
    if (rec === undefined) void uc.loadTimer(round.id);
  }, [rec, round.id, uc]);

  const state = rec?.state;
  const running = state?.running ?? null;

  // Tick only while something runs. Accuracy never depends on ticks.
  useEffect(() => {
    if (!running) {
      setNow(Date.now());
      return;
    }
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [running]);

  // Keep the screen awake while a clock runs.
  useEffect(() => {
    void setWakeLock(!!running);
    return () => void setWakeLock(false);
  }, [running]);

  // Alerts: fire once per threshold crossing, and only for crossings that just happened (not after returning from background).
  useEffect(() => {
    const from = last.current;
    last.current = now;
    if (!state || !running || now - from > 2000) return;
    const c = running === 'speech' ? state.speech : state.prep[running === 'prep0' ? 0 : 1];
    const soundMs = (settings?.timerAlertsSec ?? []).map((s) => s * 1000);
    const crossed = crossedThresholds(c, from, now, [...new Set([...FLASH_MS, ...soundMs])]).sort((a, b) => a - b);
    if (crossed.length) {
      setFlash(crossed[0]!);
      setTimeout(() => setFlash(null), 1200);
      const sound = crossed.find((t) => soundMs.includes(t));
      if (sound !== undefined) timerBeep(sound / 1000, settings?.timerVolume);
    }
  }, [now, state, running, settings?.timerAlertsSec, settings?.timerVolume]);

  const send = useCallback(
    (cmd: TimerCommand) => {
      unlockAudio(); // user gesture: lets iOS play alert sounds later
      return run(uc.timerCommand(round.id, cmd));
    },
    [round.id, uc],
  );
  const undo = useCallback(() => run(uc.timerUndo(round.id)), [round.id, uc]);

  const graceMs = round.format.graceSec * 1000;
  const view = (k: ClockKey) => (state ? viewClock(k === 'speech' ? state.speech : state.prep[k === 'prep0' ? 0 : 1], now, graceMs) : null);
  const segment = state ? round.format.segments[state.cursor] ?? null : null;

  return { rec: rec as TimerRecord | undefined, state, now, segment, view, send, undo, flash, done: !!state && state.cursor >= round.format.segments.length };
};

export type TimerApi = ReturnType<typeof useTimer>;
